import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants';
import { getAdvisorsToolbarBreadcrumbs } from '@src/components/sco/Advisors/layout/advisorsToolbarBreadcrumbs';
import { BreadcrumbItem, useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
type UseAdvisorsToolbarOptions = {
  /** Replaces the route-derived trail (still prefixed with Advisors root when using partial trails). */
  breadcrumbs?: BreadcrumbItem[] | null;
};

/**
 * Lets a section override the Advisors main-panel toolbar breadcrumbs for dynamic titles
 * (e.g. seat category, selected advisor name).
 */
export const useAdvisorsToolbar = (options?: UseAdvisorsToolbarOptions) => {
  const router = useRouter();
  const activeContent = useActiveContentContext();
  const breadcrumbs = useMemo(() => {
    if (options?.breadcrumbs?.length) {
      return options.breadcrumbs;
    }
    return getAdvisorsToolbarBreadcrumbs(router.asPath);
  }, [options?.breadcrumbs, router.asPath]);
  const breadcrumbsKey = useMemo(() => JSON.stringify(breadcrumbs), [breadcrumbs]);

  // Only re-run when the trail content changes — not when context setter identities change.
  // Depending on setSiteSection/setBreadcrumbs caused max-update-depth when the provider
  // rebuilt those functions after each breadcrumbs write.
  useEffect(() => {
    activeContent.setSiteSection?.('advisors');
    activeContent.setBreadcrumbs?.(breadcrumbs);
    return () => {
      activeContent.setBreadcrumbs?.([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: breadcrumbsKey only
  }, [breadcrumbsKey]);
  return {
    breadcrumbs
  };
};

/**
 * Prefix a trail with the Advisors root.
 * Pass `base` (from `useAdvisorsPaths().base`) first so embedded Request Workspace trails stay correct.
 * `advisorsBreadcrumbsWithTrail(base, ...trail)` or legacy `advisorsBreadcrumbsWithTrail(...trail)`.
 */
export const advisorsBreadcrumbsWithTrail = (baseOrFirst: string | BreadcrumbItem, ...rest: BreadcrumbItem[]): BreadcrumbItem[] => {
  if (typeof baseOrFirst === 'string') {
    return [{
      title: 'Advisors',
      href: baseOrFirst
    }, ...rest];
  }
  return [{
    title: 'Advisors',
    href: ADVISORS_BASE
  }, baseOrFirst, ...rest];
};